import { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaSave } from 'react-icons/fa';
import api from '../../api';
import { useColumnVisibility, ColumnPicker } from '../../components/ColumnPicker';

const empty = { name: '', client: '', active: true };

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]         = useState(empty);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    api.getAllProjects()
      .then(setProjects)
      .catch(() => setError('Failed to load projects.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(reload, [reload]);

  const openNew  = () => { setForm(empty); setEditingId(null); setShowForm(true); setError(''); };
  const openEdit = (p) => { setForm({ name: p.name, client: p.client || '', active: p.active }); setEditingId(p.id); setShowForm(true); setError(''); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await api.updateProject(editingId, form);
        setSuccess('Project updated.');
      } else {
        await api.createProject(form);
        setSuccess('Project created.');
      }
      setShowForm(false);
      reload();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    const msg = `Delete "${p.name}"? If it's used in timesheets it will be deactivated instead.`;
    if (!window.confirm(msg)) return;
    try {
      const result = await api.deleteProject(p.id);
      setSuccess(result.message || 'Done.');
      reload();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed.');
    }
  };

  const displayed = projects.filter(p => showInactive ? true : p.active);

  const PROJ_COLS = [{ key: 'client', label: 'Client' }, { key: 'status', label: 'Status' }];
  const { visible: projCols, toggle: toggleProjCol, show: showProjCol } = useColumnVisibility(PROJ_COLS, 'cols_projects');

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-10">

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#0c1b4d]">Manage Projects</h1>
            <p className="text-gray-500 text-sm mt-1">Projects available for employees to log time against.</p>
          </div>
          <button onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0c1b4d] text-white text-sm font-semibold rounded-xl hover:bg-blue-900 transition-colors">
            <FaPlus size={12} /> Add Project
          </button>
        </div>

        {error   && <p className="text-red-600 text-sm mb-4 bg-red-50 px-4 py-2 rounded-lg">{error}</p>}
        {success && <p className="text-green-700 text-sm mb-4 bg-green-50 px-4 py-2 rounded-lg">{success}</p>}

        <div className="flex items-center gap-2 mb-4">
          <input type="checkbox" id="showInactiveP" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="w-4 h-4 accent-blue-700" />
          <label htmlFor="showInactiveP" className="text-sm text-gray-600">Show inactive projects</label>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-bold text-[#0c1b4d]">{editingId ? 'Edit Project' : 'New Project'}</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
              </div>
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Project Name *</label>
                  <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    placeholder="e.g. Cloud Migration"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Client</label>
                  <input value={form.client} onChange={e => setForm({...form, client: e.target.value})}
                    placeholder="e.g. Client A  (leave blank for Internal)"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                {editingId && (
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="activeCheck" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} className="w-4 h-4 accent-blue-700" />
                    <label htmlFor="activeCheck" className="text-sm text-gray-600">Active (visible in timesheet dropdown)</label>
                  </div>
                )}
                {error && <p className="text-red-600 text-xs">{error}</p>}
                <div className="flex gap-3 pt-2">
                  <button type="submit" disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#0c1b4d] text-white text-sm font-semibold rounded-xl hover:bg-blue-900 transition-colors disabled:opacity-60">
                    <FaSave size={12} /> {saving ? 'Saving...' : 'Save Project'}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)}
                    className="px-5 py-2.5 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-slate-50">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100">
            <ColumnPicker columns={PROJ_COLS} visible={projCols} onToggle={toggleProjCol} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-slate-50">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Project Name</th>
                  {showProjCol('client') && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Client</th>}
                  {showProjCol('status') && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>}
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {displayed.length === 0 ? (
                  <tr><td colSpan={4} className="px-5 py-8 text-center text-gray-400 text-sm">No projects found.</td></tr>
                ) : displayed.map(p => (
                  <tr key={p.id} className={`border-b border-gray-50 hover:bg-slate-50 transition-colors ${!p.active ? 'opacity-50' : ''}`}>
                    <td className="px-5 py-3 font-medium text-[#0c1b4d] whitespace-nowrap">{p.name}</td>
                    {showProjCol('client') && <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{p.client || <span className="text-gray-300">Internal</span>}</td>}
                    {showProjCol('status') && <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {p.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>}
                    <td className="px-5 py-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => openEdit(p)} className="p-2 text-gray-400 hover:text-[#0c1b4d] hover:bg-blue-50 rounded-lg transition-colors"><FaEdit size={13} /></button>
                        <button onClick={() => handleDelete(p)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><FaTrash size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
